import React, { useMemo } from 'react';
import {
  getConditionsConfiguration,
  getValidatorConfigurationForAnswerType,
} from 'src/common/configMapping';
import { FormTextField } from '../FormTextField/FormTextField';
import { CustomHTML } from 'src/components/common/CustomHTML';
import RadioQuestionGroup from '../RadioQuestionGroup';
import AnnotatedDatePicker from '../AnnotatedDatePicker';
import { Text } from 'rsuite';
import { ExpirationApplicationQuestion } from 'src/types/configuration';

export interface FormQuestionProps {
  question: ExpirationApplicationQuestion;
  formValues: any;
  formError: any;
  updateFormValues: (key: string, value: any) => void;
  onRadioQuestionGroupChanged: (
    value: string,
    question: ExpirationApplicationQuestion,
    questionKey: string
  ) => void;
  answerRequired?: boolean;
  isEditAll?: boolean;
  initialFormValues?: any;
}

export const FormQuestion = ({
  question,
  formValues,
  formError,
  updateFormValues,
  onRadioQuestionGroupChanged,
  answerRequired = true,
  isEditAll = false,
  initialFormValues,
}: FormQuestionProps) => {
  // Get form values has data stored for this question
  const existingData = (formValues && formValues[question?.key ?? '']) ?? '';

  const isDisabled = useMemo(() => {
    const initialValue =
      (initialFormValues && initialFormValues[question?.key ?? '']) ?? '';
    return question?.read_only && !isEditAll && initialValue;
  }, [question, isEditAll, initialFormValues]);

  // Check whether their are conditions that constrain whether the control should be shown
  const conditionConfig =
    question?.conditions?.length && question?.conditions?.length > 0
      ? getConditionsConfiguration(question)
      : null;
  // Determine if all conditions (if any) have been meet such that the control should render
  const unmetRenderConditions =
    conditionConfig?.map(
      (configConditions: {
        evaluationFn: (arg0: any) => any;
        compareKey: string | number;
      }) => {
        if (!formValues) {
          return false;
        }
        return configConditions.evaluationFn(
          formValues[configConditions.compareKey]
        );
      }
    ) ?? [];

  // Only render if there are no unmet render conditions
  if (unmetRenderConditions.includes(false)) return;

  // Get the appropriate validator for the question type
  const validationConfig = getValidatorConfigurationForAnswerType(
    question.answer_type,
    answerRequired
  );

  // Form Text Field components
  if (
    question.answer_type === 'positive_number' ||
    question.answer_type === 'string' ||
    question.answer_type === 'currency' ||
    question.answer_type === 'text_area'
  ) {
    return (
      <FormTextField
        key={`text_field_${question.key}`}
        name={question.key ?? ''}
        label={<CustomHTML text={question.title ?? ''} />}
        boldLabel
        subtitle={() => (
          <Text className={'mb-8'}>
            <CustomHTML text={question.description ?? ''} />
          </Text>
        )}
        hasError={formError[question.key ?? '']}
        validationRule={validationConfig?.validator}
        errorMessage={validationConfig.defaultErrorMessage}
        onChange={(v) => updateFormValues(question.key ?? '', v)}
        value={existingData}
        inputWidth={'100%'}
        className="mb-16"
        inputFieldType={`${question.answer_type === 'text_area' ? 'textarea' : 'text'}`}
        useGridSizing
        disabled={isDisabled}
      />
    );
  }

  // Boolean Yes/No radio button options
  if (question.answer_type === 'boolean' || question.answer_type === 'radio') {
    const options: any[] = question.radio_options
      ? Array.from(question.radio_options)
      : [
          { key: 'Yes', label: 'Yes' },
          { key: 'No', label: 'No' },
        ];

    const selectedOption = options.find(
      (option) => option.key === existingData
    );

    // Filter Options to display just the one that matches the conditions given
    const filteredOptions = options.filter((option) => {
      if (!option.conditions) {
        return true;
      }

      const conditions = getConditionsConfiguration(option);
      return conditions
        ?.map(
          (condition: {
            evaluationFn: (arg0: any) => any;
            compareKey: string | number;
          }) => {
            if (!formValues) {
              return false;
            }

            return condition.evaluationFn(formValues[condition.compareKey]);
          }
        )
        .some((value: any) => value);
    });

    return (
      <RadioQuestionGroup
        key={`radio_group_${question.key}`}
        name={`${question.key}`}
        title={<CustomHTML text={question.title ?? ''} />}
        description={<CustomHTML text={question.description ?? ''} />}
        options={filteredOptions}
        defaultOption={selectedOption}
        value={selectedOption}
        onChange={(value: any) =>
          onRadioQuestionGroupChanged(value, question, question.key ?? '')
        }
        hasError={formError[`${question.key ?? ''}`]}
        validationRule={validationConfig.validator}
        errorMessage={validationConfig.defaultErrorMessage}
        className="mb-4"
        disabled={isDisabled}
      />
    );

    // Left the code below commented out but it may be needed since we already have a "radio" option in
    // the config and it is a bit strange to treat the "boolean" option as a radio component.

    // return (
    //     <AnnotatedCheckbox
    //         style={{marginTop: 0, marginBottom: "12px"}}
    //         name={question.key}
    //         label={question.title}
    //         onChange={(checked) => { updateFormValues(question.key, checked ? 'Yes' : 'No') }}
    //     />
    // )
  }

  // Date fields
  if (question.answer_type === 'date') {
    return (
      <AnnotatedDatePicker
        key={`tdate_picker_${question.key}`}
        name={question.key ?? ''}
        label={question.title ?? ''}
        subtitle={() => (
          <Text className="mb-8">
            <CustomHTML text={question.description ?? ''} />
          </Text>
        )}
        rule={validationConfig.validator}
        hasError={!!formError[question.key ?? '']}
        errorMessage={validationConfig.defaultErrorMessage}
        onChange={(date) => updateFormValues(question.key ?? '', date)}
        disabled={isDisabled}
        value={existingData}
      />
    );
  }
};
